import React, { useState, useEffect } from "react";
import {
  View,
  Text,
  Modal,
  TextInput,
  Pressable,
  StyleSheet,
  Platform,
} from "react-native";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";

type EditSubcategoryModalProps = {
  visible: boolean;
  onClose: () => void;
  onSave: (subcategoryName: string) => void;
  category: string;
  subcategoryName: string;
  onOpenFieldsModal?: () => void;
};

const EditSubcategoryModal: React.FC<EditSubcategoryModalProps> = ({
  visible,
  onClose,
  onSave,
  category = "—",
  subcategoryName,
  onOpenFieldsModal,
}) => {
  const [name, setName] = useState(subcategoryName);

  useEffect(() => {
    if (visible) {
      setName(subcategoryName);
    }
  }, [visible, subcategoryName]);

  const handleSave = () => {
    if (name.trim() && name !== subcategoryName) {
      onSave(name);
      onClose();
    }
  };

  const handleClose = () => {
    setName(subcategoryName);
    onClose();
  };

  const hasChanges = name.trim() !== subcategoryName;

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={handleClose}
    >
      <View style={styles.overlay}>
        <Pressable style={styles.overlay} onPress={handleClose} />
        <View style={styles.modal}>
          <View style={styles.header}>
            <Text style={styles.title}>Edit Subcategory</Text>
            <Pressable onPress={handleClose} style={styles.closeButton}>
              <MaterialIcons name="close" size={24} color="#034895" />
            </Pressable>
          </View>

          <View style={styles.body}>
            <View style={styles.field}>
              <Text style={styles.label}>Category</Text>
              <Text style={styles.value}>{category}</Text>
            </View>

            <View style={styles.field}>
              <Text style={styles.label}>
                Subcategory Name <Text style={styles.required}>*</Text>
              </Text>
              <TextInput
                style={styles.input}
                placeholder="e.g. Forklift"
                value={name}
                onChangeText={setName}
                maxLength={60}
                placeholderTextColor="#999"
              />
            </View>

            <View style={styles.field}>
              <View style={styles.fieldsRow}>
                <View style={styles.fieldsInfo}>
                  <Text style={styles.fieldLabel}>Additional Fields</Text>
                  <Text style={styles.fieldHint}>
                    Optional. Shown on the Submit Ticket form for this
                    subcategory.
                  </Text>
                </View>
                <Pressable
                  style={styles.addFieldsButton}
                  onPress={onOpenFieldsModal}
                >
                  <MaterialIcons name="settings" size={16} color="#034895" />
                  <Text style={styles.addFieldsButtonText}>Configure</Text>
                </Pressable>
              </View>
            </View>
          </View>

          <View style={styles.footer}>
            <Pressable
              style={[styles.button, styles.cancelButton]}
              onPress={handleClose}
            >
              <Text style={styles.cancelButtonText}>Cancel</Text>
            </Pressable>
            <Pressable
              style={[
                styles.button,
                styles.saveButton,
                !hasChanges && styles.saveButtonDisabled,
              ]}
              onPress={handleSave}
              disabled={!hasChanges}
            >
              <Text style={styles.saveButtonText}>Save Changes</Text>
            </Pressable>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    justifyContent: "center",
    alignItems: "center",
  },
  modal: {
    backgroundColor: "white",
    borderRadius: 8,
    minWidth: 400,
    maxWidth: 500,
    ...Platform.select({
      web: {
        maxHeight: "80vh",
      },
    }),
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: "#e0e0e0",
  },
  title: {
    fontSize: 18,
    fontWeight: "600",
    color: "#034895",
  },
  closeButton: {
    padding: 8,
  },
  body: {
    paddingHorizontal: 20,
    paddingVertical: 16,
  },
  field: {
    marginBottom: 16,
  },
  label: {
    fontSize: 14,
    fontWeight: "600",
    color: "#333",
    marginBottom: 8,
  },
  value: {
    fontSize: 14,
    fontWeight: "700",
    color: "#333",
  },
  required: {
    color: "#d32f2f",
  },
  input: {
    borderWidth: 1,
    borderColor: "#ddd",
    borderRadius: 4,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 14,
    color: "#333",
  },
  fieldsRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 12,
  },
  fieldsInfo: {
    flex: 1,
  },
  fieldLabel: {
    fontSize: 14,
    fontWeight: "600",
    color: "#333",
    marginBottom: 4,
  },
  fieldHint: {
    fontSize: 12,
    color: "#666",
  },
  addFieldsButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingHorizontal: 12,
    paddingVertical: 8,
    backgroundColor: "#f5f5f5",
    borderWidth: 1,
    borderColor: "#ddd",
    borderRadius: 4,
  },
  addFieldsButtonText: {
    fontSize: 12,
    fontWeight: "500",
    color: "#034895",
  },
  footer: {
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: 12,
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderTopWidth: 1,
    borderTopColor: "#e0e0e0",
  },
  button: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 4,
    minWidth: 100,
    justifyContent: "center",
    alignItems: "center",
  },
  cancelButton: {
    backgroundColor: "#f5f5f5",
    borderWidth: 1,
    borderColor: "#ddd",
  },
  cancelButtonText: {
    color: "#333",
    fontSize: 14,
    fontWeight: "500",
  },
  saveButton: {
    backgroundColor: "#034895",
  },
  saveButtonDisabled: {
    backgroundColor: "#cccccc",
  },
  saveButtonText: {
    color: "white",
    fontSize: 14,
    fontWeight: "500",
  },
});

export default EditSubcategoryModal;
